import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Alert,
  Form,
  InputGroup,
  Modal,
  Toast,
  ToastContainer,
  Spinner,
  Nav,
  Tab,
  ProgressBar
} from 'react-bootstrap';
import {
  Prescription,
  Clock,
  CheckCircle,
  People,
  FileText,
  Eye,
  ChevronRight,
  InfoCircle,
  Search,
  QrCode,
  Shield,
  Envelope,
  Telephone,
  Calendar,
  Droplet,
  Download,
  Printer,
  PersonCircle,
  PlusCircle,
  ArrowRight,
  Activity,
  GraphUp,
  ChevronDown,
  ChevronUp
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';

// Mock patients data
const mockPatients = [
  {
    id: 'CNAMGS-123456',
    name: 'Jean Dupont',
    birthDate: '15/05/1985',
    lastVisit: '2025-03-12',
    history: 'Hypertension, allergie aux pénicillines',
    phone: '+241 01 23 45 67',
    email: 'jean.dupont@email.ga',
    bloodType: 'A+',
    photo: 'https://randomuser.me/api/portraits/men/1.jpg',
  },
  {
    id: 'CNAMGS-789012',
    name: 'Marie Nzogho',
    birthDate: '23/09/1990',
    lastVisit: '2025-03-10',
    history: 'Asthme, diabète type 2',
    phone: '+241 01 23 45 68',
    email: 'marie.nzogho@email.ga',
    bloodType: 'O+',
    photo: 'https://randomuser.me/api/portraits/women/2.jpg',
  },
  {
    id: 'CNAMGS-345678',
    name: 'Pierre Mba',
    birthDate: '10/12/1978',
    lastVisit: '2025-03-05',
    history: 'Aucun antécédent notable',
    phone: '+241 01 23 45 69',
    email: 'pierre.mba@email.ga',
    bloodType: 'B+',
    photo: 'https://randomuser.me/api/portraits/men/3.jpg',
  },
];

// Mock prescriptions (all for the doctor "Dr. Nzogho")
const initialPrescriptions = [
  {
    id: 'P001',
    patientId: 'CNAMGS-123456',
    doctorId: 'DOC001',
    doctorName: 'Dr. Nzogho',
    date: '2025-03-12',
    medicines: [{ name: 'Paracétamol 500mg', dosage: '2x/jour', quantity: 10 }],
    status: 'pending',
  },
  {
    id: 'P002',
    patientId: 'CNAMGS-123456',
    doctorId: 'DOC001',
    doctorName: 'Dr. Nzogho',
    date: '2025-03-01',
    medicines: [{ name: 'Amoxicilline 250mg', dosage: '2x/jour', quantity: 15 }],
    status: 'dispensed',
  },
  {
    id: 'P003',
    patientId: 'CNAMGS-789012',
    doctorId: 'DOC001',
    doctorName: 'Dr. Nzogho',
    date: '2025-03-10',
    medicines: [{ name: 'Cetirizine 10mg', dosage: '1x/jour', quantity: 10 }],
    status: 'pending',
  },
  {
    id: 'P004',
    patientId: 'CNAMGS-345678',
    doctorId: 'DOC001',
    doctorName: 'Dr. Nzogho',
    date: '2025-03-05',
    medicines: [{ name: 'Ibuprofène 400mg', dosage: '1x/jour si douleur', quantity: 20 }],
    status: 'dispensed',
  },
];

const DoctorDashboard = () => {
  const { user } = useAuth();
  const [prescriptionsList, setPrescriptionsList] = useState(initialPrescriptions);
  const [searchCode, setSearchCode] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientRecord, setShowPatientRecord] = useState(false);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [prescriptionData, setPrescriptionData] = useState({
    patientId: '',
    medicines: '',
    dosage: '',
    duration: '',
  });
  const [searching, setSearching] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [filterText, setFilterText] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedRows(newSet);
  };

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 4000);
  };

  // For demo, we treat all prescriptions as belonging to the logged‑in doctor.
  // No need to filter – just display them.
  const doctorPrescriptions = prescriptionsList;

  // Filter prescriptions by patient name or medicine
  const filteredPrescriptions = doctorPrescriptions.filter((p) => {
    const patient = mockPatients.find((pat) => pat.id === p.patientId);
    const patientName = patient ? patient.name.toLowerCase() : '';
    const medicines = p.medicines.map((m) => m.name.toLowerCase()).join(' ');
    const search = filterText.toLowerCase();
    return patientName.includes(search) || medicines.includes(search);
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: (
        <Badge bg="warning" className="rounded-pill px-3 py-2">
          <Clock size={12} className="me-1" /> En attente
        </Badge>
      ),
      dispensed: (
        <Badge bg="success" className="rounded-pill px-3 py-2">
          <CheckCircle size={12} className="me-1" /> Délivrée
        </Badge>
      ),
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const handleSearchPatient = () => {
    setSearching(true);
    setTimeout(() => {
      const patient = mockPatients.find((p) => p.id === searchCode);
      if (patient) {
        setSelectedPatient(patient);
        setPrescriptionData({ ...prescriptionData, patientId: patient.id });
      } else {
        showToast('Patient non trouvé. Vérifiez le code sécurisé.', 'danger');
        setSelectedPatient(null);
      }
      setSearching(false);
    }, 500);
  };

  const handleSendPrescription = () => {
    if (!prescriptionData.medicines || !prescriptionData.dosage) {
      showToast('Veuillez remplir tous les champs obligatoires', 'warning');
      return;
    }

    const newPrescription = {
      id: `P${Date.now()}`,
      patientId: selectedPatient.id,
      doctorId: user?.id || 'DOC001',
      doctorName: user?.name || 'Dr. Nzogho',
      date: new Date().toISOString().split('T')[0],
      medicines: [{ name: prescriptionData.medicines, dosage: prescriptionData.dosage, quantity: 1 }],
      status: 'pending',
    };

    setPrescriptionsList((prev) => [...prev, newPrescription]);
    setShowPrescriptionModal(false);
    setPrescriptionData({ patientId: '', medicines: '', dosage: '', duration: '' });
    showToast('Prescription envoyée à la pharmacie avec succès !', 'success');
  };

  const stats = useMemo(
    () => ({
      totalPatients: doctorPrescriptions.length,
      totalPrescriptions: doctorPrescriptions.length,
      pending: doctorPrescriptions.filter((p) => p.status === 'pending').length,
      dispensed: doctorPrescriptions.filter((p) => p.status === 'dispensed').length,
    }),
    [doctorPrescriptions]
  );

  const statsCards = [
    {
      title: 'Patients traités',
      value: stats.totalPatients,
      icon: People,
      color: '#4361ee',
      gradient: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)',
      bg: 'white',
    },
    {
      title: 'Prescriptions',
      value: stats.totalPrescriptions,
      icon: Prescription,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      bg: 'white',
    },
    {
      title: 'En attente',
      value: stats.pending,
      icon: Clock,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      bg: 'white',
    },
    {
      title: 'Délivrées',
      value: stats.dispensed,
      icon: CheckCircle,
      color: '#ef4444',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      bg: 'white',
    },
  ];

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ToastContainer
        className="p-3"
        style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}
      >
        <Toast
          show={toast.show}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
          bg={toast.variant}
          delay={4000}
          autohide
        >
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success'
                ? 'Succès'
                : toast.variant === 'danger'
                ? 'Erreur'
                : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* Welcome header */}
        <div className="welcome-section mb-4 p-4 rounded-4" style={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
        }}>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-circle p-3">
                  <PersonCircle size={48} style={{color:'#1e3c72'}} />
                </div>
                <div>
                  <h1 className="h3 fw-bold mb-1">
                    Bonjour, {user?.name?.split(' ')[0] || 'Dr. Nzogho'} !
                  </h1>
                  <p className="mb-0 opacity-90">
                    Voici le résumé de votre activité médicale aujourd'hui.
                  </p>
                  <div className="mt-2">
                    <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                      <Activity className="me-1" size={14} />
                      12 patients aujourd'hui
                    </Badge>
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button variant="light" className="rounded-pill px-4 me-2">
                <Download size={16} className="me-2" /> Exporter
              </Button>
              <Button variant="outline-light" className="rounded-pill px-4">
                <Printer size={16} className="me-2" /> Imprimer
              </Button>
            </Col>
          </Row>
        </div>

        {/* Stats Cards */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={6} lg={3} xs={6} key={idx}>
              <Card
                className="border-0 shadow-sm overflow-hidden stats-card h-100"
                style={{ borderRadius: '16px', background: card.bg }}
              >
                <div className="stats-card-gradient" style={{ background: card.gradient, height: '4px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="text-uppercase text-muted mb-1 small fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        {card.title}
                      </h6>
                      <h3 className="mb-0 fw-bold" style={{ fontSize: '1.6rem', lineHeight: 1.2 }}>
                        {card.value}
                      </h3>
                    </div>
                    <div className="stats-icon rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                         style={{ background: card.gradient, width: '44px', height: '44px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                      <card.icon size={22} className="text-white" />
                    </div>
                  </div>
                  <div className="progress-wrapper mt-auto">
                    <div className="d-flex justify-content-between mb-0">
                      <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                        {card.title === 'Patients traités' ? 'Consultations' : 'Progression'}
                      </small>
                      <small className="fw-medium" style={{ fontSize: '0.7rem' }}>
                        {Math.round((card.value / (stats.totalPatients || 1)) * 100)}%
                      </small>
                    </div>
                    <ProgressBar
                      now={(card.value / (stats.totalPatients || 1)) * 100}
                      variant={card.color === '#4361ee' ? 'primary' : card.color === '#10b981' ? 'success' : card.color === '#f59e0b' ? 'warning' : 'danger'}
                      className="stats-progress"
                      style={{ height: '4px' }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Patient search card */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm bg-white" style={{ borderRadius: '16px' }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                    <QrCode size={20} className="text-primary" />
                  </div>
                  <h5 className="fw-semibold mb-0">Accès au dossier patient</h5>
                </div>
                <p className="text-muted small mb-3">
                  Utilisez le code sécurisé présent sur la carte électronique du patient
                </p>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-0">
                    <QrCode size={16} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Scanner ou saisir le code patient (ex: CNAMGS-123456)"
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value)}
                    className="border-0 bg-light"
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchPatient()}
                  />
                  <Button
                    variant="primary"
                    onClick={handleSearchPatient}
                    disabled={searching}
                    className="rounded-pill px-4"
                  >
                    {searching ? <Spinner size="sm" className="me-1" /> : <Search size={16} className="me-1" />}
                    {searching ? 'Recherche...' : 'Rechercher'}
                  </Button>
                </InputGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Patient details (if selected) */}
        {selectedPatient && (
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <div className="p-4" style={{ background: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)' }}>
                  <div className="d-flex align-items-center gap-2">
                    <FileText size={20} className="text-white" />
                    <h5 className="mb-0 fw-semibold text-white">Dossier patient</h5>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <Row>
                    <Col md={4} className="text-center">
                      <div className="position-relative d-inline-block">
                        <div
                          style={{
                            width: '120px',
                            height: '120px',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: `4px solid #fff`,
                            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                            margin: '0 auto',
                            background: '#f0f0f0',
                          }}
                        >
                          <img
                            src={selectedPatient.photo}
                            alt="Photo patient"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </div>
                        <div
                          className="position-absolute bottom-0 end-0 bg-primary rounded-circle p-2"
                          style={{ transform: 'translate(10%, 10%)' }}
                        >
                          <CheckCircle size={16} className="text-white" />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Badge bg="success" className="rounded-pill px-3 py-2">
                          <CheckCircle size={12} className="me-1" /> Dossier actif
                        </Badge>
                      </div>
                    </Col>
                    <Col md={8}>
                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <small className="text-muted d-block">Nom complet</small>
                            <strong className="fs-5">{selectedPatient.name}</strong>
                          </div>
                          <div className="mb-3">
                            <small className="text-muted d-block">Numéro d'assuré</small>
                            <strong className="fs-5">{selectedPatient.id}</strong>
                          </div>
                          <div className="mb-3">
                            <small className="text-muted d-block">Date de naissance</small>
                            <strong>{selectedPatient.birthDate}</strong>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <small className="text-muted d-block">Dernière visite</small>
                            <strong>{formatDate(selectedPatient.lastVisit)}</strong>
                          </div>
                          <div className="mb-3">
                            <small className="text-muted d-block">Groupe sanguin</small>
                            <strong>
                              <Droplet size={12} className="text-danger me-1" /> {selectedPatient.bloodType}
                            </strong>
                          </div>
                          <div className="mb-3">
                            <small className="text-muted d-block">Contact</small>
                            <div>
                              <Telephone size={12} className="me-1" /> {selectedPatient.phone}
                            </div>
                            <div>
                              <Envelope size={12} className="me-1" /> {selectedPatient.email}
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <div className="mt-2 p-3 rounded-3" style={{ background: '#f8f9fa', borderLeft: '4px solid #f59e0b' }}>
                        <small className="text-muted d-block mb-1">Antécédents médicaux</small>
                        <strong>{selectedPatient.history}</strong>
                      </div>
                    </Col>
                  </Row>
                  <div className="d-flex gap-3 mt-4">
                    <Button
                      variant="success"
                      onClick={() => setShowPrescriptionModal(true)}
                      className="rounded-pill px-4"
                    >
                      <Prescription size={16} className="me-1" /> Prescrire
                    </Button>
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowPatientRecord(true)}
                      className="rounded-pill px-4"
                    >
                      <Eye size={16} className="me-1" /> Dossier complet
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Prescriptions list */}
        <Row className="mb-4">
          <Col lg={12}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <div className="bg-success bg-opacity-10 rounded-circle p-2">
                      <Prescription size={18} className="text-success" />
                    </div>
                    <h5 className="fw-semibold mb-0">Mes prescriptions récentes</h5>
                  </div>
                  <div className="d-flex gap-2">
                    <InputGroup style={{ width: '250px' }}>
                      <InputGroup.Text className="bg-light border-0">
                        <Search size={14} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Rechercher un patient ou médicament..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        className="border-0 bg-light"
                      />
                    </InputGroup>
                    <Button variant="outline-primary" size="sm" className="rounded-pill">
                      <PlusCircle size={14} className="me-1" /> Nouvelle prescription
                    </Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Date</th>
                        <th>Patient</th>
                        <th className="d-none d-sm-table-cell">Médicaments</th>
                        <th>Statut</th>
                        <th className="d-none d-sm-table-cell">Suivi pharmacie</th>
                        <th className="d-sm-none text-center">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPrescriptions.length > 0 ? (
                        filteredPrescriptions.map((p) => {
                          const patient = mockPatients.find((pat) => pat.id === p.patientId);
                          const isExpanded = expandedRows.has(p.id);
                          const pharmacyStatus = p.status === 'dispensed' ? (
                            <Badge bg="success" className="rounded-pill px-3 py-2">
                              <CheckCircle size={12} className="me-1" /> Délivrée en pharmacie
                            </Badge>
                          ) : (
                            <Badge bg="warning" className="rounded-pill px-3 py-2">
                              <Clock size={12} className="me-1" /> En attente de délivrance
                            </Badge>
                          );
                          return (
                            <React.Fragment key={p.id}>
                              {/* Main row */}
                              <tr className="align-middle">
                                <td data-label="Date" className="small">
                                  <div className="fw-medium">{formatDate(p.date)}</div>
                                  <small className="text-muted d-sm-none">N° {p.id}</small>
                                </td>
                                <td data-label="Patient" className="fw-medium">
                                  {patient ? patient.name : p.patientId}
                                </td>
                                <td data-label="Médicaments" className="d-none d-sm-table-cell small text-muted">
                                  {p.medicines.map((m) => m.name).join(', ')}
                                </td>
                                <td data-label="Statut">{getStatusBadge(p.status)}</td>
                                <td data-label="Suivi pharmacie" className="d-none d-sm-table-cell">
                                  {pharmacyStatus}
                                </td>
                                <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                                  <Button
                                    variant="link"
                                    onClick={() => toggleRow(p.id)}
                                    className="p-0 text-secondary"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                  </Button>
                                </td>
                              </tr>
                              {/* Expanded row - visible only on mobile */}
                              {isExpanded && (
                                <tr className="d-sm-none detail-row">
                                  <td colSpan="6" className="p-3 bg-light">
                                    <div className="row g-2">
                                      <div className="col-12">
                                        <strong>Médicaments :</strong> {p.medicines.map((m) => m.name).join(', ')}
                                      </div>
                                      <div className="col-12">
                                        <strong>Posologie :</strong> {p.medicines.map((m) => m.dosage).join(', ')}
                                      </div>
                                      <div className="col-12">
                                        <strong>Suivi pharmacie :</strong> {pharmacyStatus}
                                      </div>
                                      <div className="col-12">
                                        <strong>Prescription N° :</strong> {p.id}
                                      </div>
                                      <div className="col-12">
                                        <strong>Médecin :</strong> {p.doctorName}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <Prescription size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune prescription trouvée</p>
                            {selectedPatient && (
                              <Button
                                variant="primary"
                                onClick={() => setShowPrescriptionModal(true)}
                                className="rounded-pill"
                              >
                                Créer une prescription
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0 pt-3 pb-4">
                <Button variant="outline-primary" size="sm" className="w-100 rounded-pill">
                  <FileText size={14} className="me-1" />
                  Voir toutes mes prescriptions
                </Button>
              </Card.Footer>
            </Card>
          </Col>
        </Row>

        {/* Info banner */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert
              variant="info"
              className="border-0 rounded-3 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)' }}
            >
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Suivi hôpital-patient-pharmacie</strong>
                  <span className="small">
                    Les prescriptions sont automatiquement transmises à la pharmacie choisie par le
                    patient. Vous pouvez suivre leur statut (en attente de délivrance / délivrée) dans
                    la liste ci-dessus.
                  </span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        {/* Modals (unchanged from original) */}
        <Modal show={showPatientRecord} onHide={() => setShowPatientRecord(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="d-flex align-items-center gap-2">
              <FileText size={20} />
              Dossier médical complet
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPatient && (
              <>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                      <small className="text-muted d-block">Nom complet</small>
                      <strong>{selectedPatient.name}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                      <small className="text-muted d-block">Numéro d'assuré</small>
                      <strong>{selectedPatient.id}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                      <small className="text-muted d-block">Date de naissance</small>
                      <strong>{selectedPatient.birthDate}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                      <small className="text-muted d-block">Dernière visite</small>
                      <strong>{formatDate(selectedPatient.lastVisit)}</strong>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                      <small className="text-muted d-block">Antécédents médicaux</small>
                      <strong>{selectedPatient.history}</strong>
                    </div>
                  </Col>
                </Row>
                <hr />
                <h6 className="fw-semibold mb-3">Historique des prescriptions</h6>
                <Table responsive size="sm">
                  <thead className="bg-light">
                    <tr>
                      <th>Date</th>
                      <th>Médicaments</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctorPrescriptions
                      .filter((p) => p.patientId === selectedPatient.id)
                      .map((p) => (
                        <tr key={p.id}>
                          <td>{formatDate(p.date)}</td>
                          <td>{p.medicines.map((m) => m.name).join(', ')}</td>
                          <td>{getStatusBadge(p.status)}</td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPatientRecord(false)}>
              Fermer
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showPrescriptionModal} onHide={() => setShowPrescriptionModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-success text-white">
            <Modal.Title className="d-flex align-items-center gap-2">
              <Prescription size={20} />
              Prescription électronique
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPatient && (
              <>
                <div className="mb-4 p-3 rounded-3" style={{ background: '#e8f5e9', borderLeft: '4px solid #10b981' }}>
                  <div className="d-flex align-items-center gap-2">
                    <People size={18} className="text-success" />
                    <strong>Patient :</strong> {selectedPatient.name}
                    <span className="text-muted">({selectedPatient.id})</span>
                  </div>
                </div>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Médicament(s) <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Ex: Paracétamol 500mg, Amoxicilline 250mg"
                      value={prescriptionData.medicines}
                      onChange={(e) => setPrescriptionData({ ...prescriptionData, medicines: e.target.value })}
                      className="border-0 bg-light"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Posologie <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ex: 2 comprimés par jour pendant 5 jours"
                      value={prescriptionData.dosage}
                      onChange={(e) => setPrescriptionData({ ...prescriptionData, dosage: e.target.value })}
                      className="border-0 bg-light"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Durée du traitement</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ex: 7 jours"
                      value={prescriptionData.duration}
                      onChange={(e) => setPrescriptionData({ ...prescriptionData, duration: e.target.value })}
                      className="border-0 bg-light"
                    />
                  </Form.Group>
                </Form>
                <Alert variant="info" className="mt-3">
                  <InfoCircle size={14} className="me-1" />
                  La prescription sera envoyée automatiquement à la pharmacie choisie par le patient.
                </Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPrescriptionModal(false)} className="rounded-pill">
              Annuler
            </Button>
            <Button variant="success" onClick={handleSendPrescription} className="rounded-pill px-4">
              <CheckCircle size={16} className="me-1" /> Envoyer
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          .stats-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.15) !important;
          }
          .stats-card-gradient {
            transition: height 0.2s ease;
          }
          .stats-card:hover .stats-card-gradient {
            height: 6px;
          }
          .stats-icon {
            transition: transform 0.2s ease;
          }
          .stats-card:hover .stats-icon {
            transform: scale(1.05);
          }
          .stats-progress {
            background-color: #e9ecef;
            border-radius: 4px;
          }
          .stats-progress .progress-bar {
            border-radius: 4px;
          }
          /* Responsive table adjustments */
          @media (max-width: 768px) {
            .table td, .table th {
              padding: 0.5rem;
            }
            .table td[data-label] {
              display: table-cell;
            }
            .detail-row td {
              padding: 1rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default DoctorDashboard;